import { useCallback, useEffect, useRef } from 'react';

/**
 * Custom hook to manage Fillout popup form
 * 
 * This hook provides a stable, reliable way to open the Fillout contact popup.
 * It handles script loading and uses multiple fallback methods to ensure
 * the popup works:
 * - After page refresh
 * - After SPA navigation
 * - On first click without refresh
 * - Across multiple open/close cycles
 */

const FILLOUT_SCRIPT_URL = 'https://server.fillout.com/embed/v1/';
const FILLOUT_FORM_ID = 'd16QMXrZHJus';

// Global singleton to track script loading state across all hook instances
let globalScriptLoaded = false;
let globalScriptLoading = false;

export const useFillout = () => {
  const mountedRef = useRef(true);
  const openAttemptRef = useRef(0);

  // Load script on mount (idempotent - only loads once globally)
  useEffect(() => {
    mountedRef.current = true;

    const loadScript = () => {
      // Already loaded or loading
      if (globalScriptLoaded || globalScriptLoading) {
        return;
      }

      // Check if script element already exists
      const existingScript = document.querySelector(`script[src="${FILLOUT_SCRIPT_URL}"]`);
      if (existingScript) {
        globalScriptLoaded = true;
        return;
      }

      globalScriptLoading = true;

      const script = document.createElement('script');
      script.src = FILLOUT_SCRIPT_URL;
      script.async = true;

      script.onload = () => {
        globalScriptLoaded = true;
        globalScriptLoading = false;
      };

      script.onerror = () => {
        globalScriptLoading = false;
        console.error('Failed to load Fillout script');
      };

      // Append to head (not body) for better reliability
      document.head.appendChild(script);
    };

    loadScript();

    return () => {
      mountedRef.current = false;
    };
    // Note: No cleanup of script - it should persist across navigation.
    // Module-level globals and in-effect locals are intentionally not declared as deps.
    // Intentional: mount-only effect; deps intentionally omitted
  }, []);

  // Open the Fillout popup using multiple methods for reliability
  const openFilloutPopup = useCallback(() => {
    openAttemptRef.current += 1;
    const currentAttempt = openAttemptRef.current;

    const tryOpen = (retries = 0) => {
      // Abort if component unmounted or a newer attempt started
      if (!mountedRef.current || currentAttempt !== openAttemptRef.current) {
        return;
      }

      // Method 1: Try Fillout.openPopup (newer API)
      if (window.Fillout && typeof window.Fillout.openPopup === 'function') {
        try {
          window.Fillout.openPopup(FILLOUT_FORM_ID);
          return;
        } catch (e) {
          console.warn('Fillout.openPopup failed, falling through:', e);
        }
      }

      // Method 2: Try Fillout.open (older API)
      if (window.Fillout && typeof window.Fillout.open === 'function') {
        try {
          window.Fillout.open(FILLOUT_FORM_ID);
          return;
        } catch (e) {
          console.warn('Fillout.open failed, falling through:', e);
        }
      }

      // Method 3: Find the container and trigger click
      const container = document.querySelector(`[data-fillout-id="${FILLOUT_FORM_ID}"]`);
      if (container) {
        // Look for Fillout-generated button or trigger
        const button = container.querySelector('button') || 
                      container.querySelector('[data-fillout-button]') ||
                      container.querySelector('a');
        
        if (button) {
          button.click();
          return;
        }

        // Method 4: Try clicking the container itself (some Fillout versions)
        if (container.click) {
          container.click();
          // Check if it worked by looking for the popup
          setTimeout(() => {
            const popup = document.querySelector('.fillout-popup, .fillout-modal, [class*="fillout"]');
            if (popup) return; // Success
            
            // If no popup and retries left, try again
            if (retries < 3) {
              tryOpen(retries + 1);
            }
          }, 200);
          return;
        }
      }

      // Retry with backoff if methods failed (script might still be initializing)
      if (retries < 5) {
        const delay = Math.min(100 * (retries + 1), 500);
        setTimeout(() => tryOpen(retries + 1), delay);
      }
    };

    // Start attempting to open with a small initial delay for stability
    setTimeout(() => tryOpen(), 50);
    // Module-level constants and in-function locals are intentionally not declared as deps.
    // Intentional: mount-only effect; deps intentionally omitted
  }, []);

  return { 
    openFilloutPopup,
    // Kept for backward compatibility
    filloutReady: globalScriptLoaded
  };
};

export default useFillout;
